import React from 'react';
import { WidgetTooltipContentProps } from '@components/Widgets/WidgetTooltip/WidgetTooltip';
import { generateInnerDefaultFormatter } from '@fabric-ui/components/Table';
import { Box } from '@mui/material';
import {
  getDateBucketFromExpression,
  getFieldTypeByExpression,
  isDateTruncExpression,
} from '@views/CustomDashboards/CustomDashboardsUtils/expressionUtils';
import { getFieldType } from '@views/CustomDashboards/getFieldType';
import { Measurement } from '@views/Measurements/types';
import { AvComposedWidget } from '../../../../../components/Widgets/AvComposedWidget';
import { NO_VALUE, stackedOption, StackedType } from '../../../constants';
import { getRepeatedValueToString } from '../../../CustomDashboardsUtils';
import { AllTypeWidgets } from '../../../types';
import { clickthroughMetricsPrefix, clickthroughWidgetId } from '../../Clickthrough/consts';
import { getParamFromURL } from '../../Clickthrough/utils';
import {
  formatStacked100Percentage,
  formatTooltipNumberValue,
  getStacked100AbsoluteValue,
  TOOLTIP_SECONDARY_VALUE_MIN_WIDTH,
} from '../tooltipNumberFormatter';

export const getBarQueryType = ({ dims, metrics }) =>
  dims.length === 1 && metrics.length === 1
    ? BarVisualizationType.Normal
    : dims.length === 1 && metrics.length > 1
      ? BarVisualizationType.MultiMetric
      : BarVisualizationType.MultiDims;

export enum BarVisualizationType {
  Normal,
  MultiMetric,
  MultiDims,
}

type TooltipSharedProps = Pick<WidgetTooltipContentProps, 'payload' | 'shared' | 'activeCellDataKey'> & {
  widget: AllTypeWidgets;
  measurements: Record<string, Measurement>;
  tooltipProps: React.ComponentProps<typeof AvComposedWidget>['tooltipProps'];
  fieldTypeMap: Record<string, any>;
  widgetData?: Array<Record<string, any>>;
};

type Stacked100Context = { isStacked100: boolean; stacked100Row?: Record<string, any> };

const getStacked100Context = (
  widget: AllTypeWidgets,
  widgetData: Array<Record<string, any>> | undefined,
  name: string
): Stacked100Context => {
  const isStacked100 = stackedOption[widget.type] === StackedType.Stacked100;
  return { isStacked100, stacked100Row: isStacked100 ? widgetData?.find(row => row.name === name) : undefined };
};

const getTooltipValueFields = (
  rawValue: unknown,
  dataKey: string | number | undefined,
  { isStacked100, stacked100Row }: Stacked100Context
): { value: React.ReactNode } => {
  if (!isStacked100) return { value: formatTooltipNumberValue(rawValue) };
  const absolute = getStacked100AbsoluteValue(stacked100Row, dataKey);
  const percentage = formatStacked100Percentage(rawValue);
  return {
    value: (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
        {absolute != null && <span>{absolute}</span>}
        {percentage != null && (
          <Box component="span" sx={{ fontWeight: 400, minWidth: TOOLTIP_SECONDARY_VALUE_MIN_WIDTH, textAlign: 'right', flexShrink: 0 }}>
            {percentage}
          </Box>
        )}
      </Box>
    ),
  };
};

const getMultiMeasurementsBarWidgetTooltipContentProps = (
  props: TooltipSharedProps
): Pick<WidgetTooltipContentProps, 'titleDotColor' | 'title' | 'content'> => {
  const { widget, payload = [], measurements, activeCellDataKey, tooltipProps, fieldTypeMap, widgetData } = props;
  const { color, payload: firstCellPayload } = payload?.at(0) || {};
  const { name } = (Array.isArray(firstCellPayload) ? firstCellPayload[0] : firstCellPayload) || {};
  const { requests } = widget;
  const { metrics, dims } = requests[0].select;
  const { description: measurementDescription, displayName: measurementDisplayName } =
    measurements?.[metrics[0].alias || metrics[0].name] || {};

  const secondSelectedDim = dims[1];
  const dimType = getFieldType({
    entityFieldKey: secondSelectedDim.name,
    measurements: Object.values(measurements),
    fieldTypeMap,
  });
  const stacked100Context = getStacked100Context(widget, widgetData, name);

  const content: WidgetTooltipContentProps['content'] = [
    { title: measurementDisplayName, description: measurementDescription },
    ...payload
      .filter(x => !activeCellDataKey || x.dataKey === activeCellDataKey)
      .map(({ color, value, name: payloadName, dataKey }) => ({
        dotColor: color,
        title: generateInnerDefaultFormatter({
          value: payloadName,
          type: secondSelectedDim.expression ? getFieldTypeByExpression(secondSelectedDim.expression) : dimType,
          fieldName:
            secondSelectedDim.expression && isDateTruncExpression(secondSelectedDim.expression)
              ? getDateBucketFromExpression(secondSelectedDim.expression)
              : undefined,
          shouldDisplayNull: true,
        }),
        ...getTooltipValueFields(value, dataKey, stacked100Context),
      })),
  ];

  return {
    content,
    title: tooltipProps?.titleFormatter?.(name) || name,
    titleDotColor: color,
  };
};

export const getBarWidgetTooltipContentProps = (
  props: TooltipSharedProps
): Pick<WidgetTooltipContentProps, 'titleDotColor' | 'title' | 'content'> => {
  const { widget, payload = [], measurements, tooltipProps, activeCellDataKey, widgetData } = props;

  const { color, payload: firstCellPayload } = payload?.at(0) || {};
  const { name } = (Array.isArray(firstCellPayload) ? firstCellPayload[0] : firstCellPayload) || {};

  const { requests } = widget;
  const { dims: dimensions, metrics } = requests[0].select;
  const barQueryType = getBarQueryType(requests[0].select);
  if (barQueryType === BarVisualizationType.MultiDims) {
    return getMultiMeasurementsBarWidgetTooltipContentProps(props);
  }

  const totalDimensions = dimensions.length;
  const totalMetrics = metrics.length;
  const { description: measurementDescription, displayName: measurementDisplayName } =
    measurements?.[metrics[0].alias || metrics[0].name] || {};
  const stacked100Context = getStacked100Context(widget, widgetData, name);

  const content: WidgetTooltipContentProps['content'] = payload
    ?.filter(x => !activeCellDataKey || x.dataKey === activeCellDataKey)
    .map(({ color, dataKey, value }) => ({
      dotColor: totalMetrics > 1 ? color : undefined,
      title: totalMetrics > 1 && dataKey ? measurements?.[dataKey]?.displayName : measurementDisplayName,
      description: totalMetrics > 1 && dataKey ? measurements?.[dataKey]?.description : measurementDescription,
      ...getTooltipValueFields(value, dataKey, stacked100Context),
    }));

  if (totalDimensions > 1) {
    content.push({ title: tooltipProps?.metricFormatter?.(name) || name, dotColor: color, titleProps: { fontWeight: 400 } });
  }

  return {
    content,
    title: name,
    titleDotColor: totalDimensions === 1 && totalMetrics === 1 ? color : undefined,
  };
};

export const getSelectedClickthroughValue = ({
  filters,
  widget,
  barQueryType,
  clickthroughDims,
  activeIndex,
}: {
  filters: Record<string, any>;
  widget: AllTypeWidgets;
  barQueryType: BarVisualizationType;
  clickthroughDims: Record<string, any>;
  activeIndex: number;
}) => {
  const { dims } = widget.requests[0].select;
  if (getParamFromURL<string>(filters[clickthroughWidgetId]) !== widget.id) {
    return [];
  }

  if (barQueryType === BarVisualizationType.Normal) {
    return [{ name: getRepeatedValueToString(Object.values(clickthroughDims)?.[activeIndex]?.[0]) }];
  }

  if (barQueryType === BarVisualizationType.MultiDims) {
    return [
      {
        name: getRepeatedValueToString(clickthroughDims[dims[0].name]?.[0]) ?? NO_VALUE,
        dataKey: clickthroughDims[dims[1].name]?.[0],
      },
    ];
  }

  // Default case for MultiMetric
  return [
    {
      name: getRepeatedValueToString(clickthroughDims[dims[0].name]?.[0]) ?? NO_VALUE,
      dataKey: getParamFromURL<string>(filters[clickthroughMetricsPrefix]),
    },
  ];
};
